// ============================================================
// App-wide constants — NO hardcoded values in components.
// ============================================================

export const APP_NAME = 'AI News Template Renderer';
export const APP_VERSION = '1.0.0-mvp';

// API base URL.
// In production the React build is served by the same Express server,
// so we use a relative path ('') — all /api/* calls go to the same origin.
// In dev, Vite proxies /api/* to localhost:4000 (see vite.config.ts),
// so relative paths work there too.
// Override with VITE_API_BASE_URL env var if you need an absolute URL.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const API_BASE_URL: string =
  ((import.meta as any).env?.VITE_API_BASE_URL as string | undefined) ?? '';

// Accepted MIME types per asset category
export const ACCEPTED_MIME_TYPES = {
  logo: ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'],
  anchorVideo: ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'],
  // Audio: include MP3, WAV, OGG, M4A, AAC, and MP4-audio
  voiceAudio: ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/mp4', 'audio/m4a', 'audio/x-m4a', 'audio/aac', 'audio/x-aac'],
  backgroundMusic: ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/mp4', 'audio/m4a', 'audio/x-m4a', 'audio/aac', 'audio/x-aac'],
  backgroundImage: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  backgroundVideo: ['video/mp4', 'video/webm', 'video/quicktime'],
  watermark: ['image/png', 'image/svg+xml', 'image/webp'],
  // Dynamic image overlay slots (image1…image4)
  image1: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'],
  image2: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'],
  image3: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'],
  image4: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'],
  // Dynamic video overlay slots
  video1: ['video/mp4', 'video/webm', 'video/quicktime'],
  video2: ['video/mp4', 'video/webm', 'video/quicktime'],
  // Generic fallbacks used by validation for unknown keys
  image: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'],
  video: ['video/mp4', 'video/webm'],
} as const;

// Max file sizes in bytes
export const MAX_FILE_SIZES = {
  image: 20 * 1024 * 1024,    // 20 MB
  video: 500 * 1024 * 1024,   // 500 MB
  audio: 50 * 1024 * 1024,    // 50 MB
} as const;

// Output presets
export const OUTPUT_PRESETS = {
  HD_30: { resolution: '1920x1080' as const, fps: 30 as const, bitrate: '8M', codec: 'H264' as const, format: 'MP4' as const },
  HD_60: { resolution: '1920x1080' as const, fps: 60 as const, bitrate: '16M', codec: 'H264' as const, format: 'MP4' as const },
  SD_30: { resolution: '1280x720' as const, fps: 30 as const, bitrate: '4M', codec: 'H264' as const, format: 'MP4' as const },
  SHORTS_30: { resolution: '1080x1920' as const, fps: 30 as const, bitrate: '8M', codec: 'H264' as const, format: 'MP4' as const },
} as const;

// Available templates (IDs must match JSON config filenames)
export const TEMPLATE_IDS = ['classic-news', 'breaking-news', 'youtube-shorts'] as const;

// SSE event types for render progress
export const SSE_EVENTS = {
  PROGRESS: 'progress',
  COMPLETE: 'complete',
  ERROR: 'error',
} as const;

// Preview dimensions (scaled down from full resolution for the UI)
export const PREVIEW_SCALE = 0.35;
